import '@fancyapps/fancybox';
import '@fancyapps/fancybox/dist/jquery.fancybox.min.css';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import Alpine from 'alpinejs';
import 'flowbite';
import { createApp } from 'vue';
import NotificationModal from './components/Shared/NotificationModal.vue';

window.Alpine = Alpine;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get CSRF token from meta tag
 */
function getCsrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
}

// ============================================================================
// MODAL MANAGEMENT
// ============================================================================

const ModalManager = {
    modal: null,
    modalClose: null,
    modalBackdrop: null,
    modalVideo: null,
    modalVideoSrc: null,

    init() {
        this.modal = document.querySelector('.video-modal');
        this.modalClose = document.querySelector('.modal-close');
        this.modalBackdrop = document.querySelector('.modal-backdrop');
        this.modalVideo = document.querySelector('.modal-video');
        this.modalVideoSrc = document.querySelector('.modal-video source');
        this.bindEvents();
    },

    bindEvents() {
        if (this.modalClose) {
            this.modalClose.addEventListener('click', () => this.close());
        }
        if (this.modalBackdrop) {
            this.modalBackdrop.addEventListener('click', () => this.close());
        }
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.modal && !this.modal.classList.contains('hidden')) {
                this.close();
            }
        });
    },

    open(videoUrl) {
        if (!this.modalVideoSrc || !this.modalVideo) {
            console.error('Modal video elements not found');
            return;
        }
        this.modalVideoSrc.src = videoUrl;
        this.modalVideo.load();
        this.modal.classList.remove('hidden');
        document.body.style.overflow = 'hidden';
    },

    close() {
        if (!this.modal || !this.modalVideo) return;
        this.modal.classList.add('hidden');
        this.modalVideo.pause();
        this.modalVideo.currentTime = 0;
        document.body.style.overflow = '';
    },
};

// ============================================================================
// POPUP NOTIFICATION MANAGEMENT
// ============================================================================

const PopupNotificationManager = {
    init() {
        if (!window.popupNotifications || window.popupNotifications.length === 0) {
            return;
        }

        if (!window.notificationModal || typeof window.notificationModal.openQueue !== 'function') {
            return;
        }

        window.notificationModal.openQueue(window.popupNotifications);
    },
};

// ============================================================================
// ATTACHMENT MANAGEMENT
// ============================================================================

const AttachmentManager = {
    deletedAttachmentIDs: [],
    initialized: false,
    defaults: {
        lead: {
            maxUploadBytes: 1024 * 1024,
            acceptedMimeTypes: ['image/jpeg', 'image/png', 'image/webp'],
            acceptedNamePattern: /\.(jpe?g|png|webp)$/i,
        },
        profile: {
            maxUploadBytes: 1024 * 1024,
            acceptedMimeTypes: ['image/jpeg', 'image/png', 'image/webp'],
            acceptedNamePattern: /\.(jpe?g|png|webp)$/i,
        },
    },

    init() {
        if (this.initialized) return;
        this.initialized = true;
        this.bindAddEvents();
        this.bindRemoveEvents();
        this.bindExistingFileInputs();
        this.insertUploadHints();
    },

    getContextForInput(input) {
        const form = input?.closest('form');
        if (!form) return 'lead';
        if (form.classList.contains('profile-details-form')) return 'profile';
        if (form.classList.contains('amicus-form') || form.id === 'support-form') return 'lead';
        return 'lead';
    },

    getRulesForInput(input) {
        const context = this.getContextForInput(input);
        return this.defaults[context] || this.defaults.lead;
    },

    formatBytes(bytes) {
        const mb = bytes / (1024 * 1024);
        if (Number.isFinite(mb) && mb >= 1) {
            return `${Math.round(mb)}MB`;
        }
        const kb = bytes / 1024;
        return `${Math.round(kb)}KB`;
    },

    getLimitForAttachmentsContainer(attachmentsContainer) {
        const addButton = attachmentsContainer?.nextElementSibling;
        if (addButton && addButton.classList.contains('add-attachment')) {
            const limit = parseInt(addButton.getAttribute('data-attachment-limit'), 10);
            return Number.isFinite(limit) && limit > 0 ? limit : 1;
        }
        return 1;
    },

    insertUploadHints() {
        document.querySelectorAll('.attachments').forEach((attachmentsContainer) => {
            if (!attachmentsContainer || attachmentsContainer.querySelector('.attachment-upload-hint')) return;

            const input = attachmentsContainer.querySelector('input[type="file"]');
            if (!input) return;

            const rules = this.getRulesForInput(input);
            const limit = this.getLimitForAttachmentsContainer(attachmentsContainer);

            const hint = document.createElement('p');
            hint.className = 'text-xs text-gray-500 mt-2 attachment-upload-hint';

            const types = rules.acceptedMimeTypes.includes('image/webp') ? 'JPG, PNG, or WebP' : 'JPG or PNG';
            const maxSize = this.formatBytes(rules.maxUploadBytes);
            hint.textContent =
                limit > 1 ? `Up to ${limit} images. ${types}. Max ${maxSize} each.` : `${types}. Max ${maxSize}.`;

            attachmentsContainer.insertAdjacentElement('afterend', hint);
        });
    },

    bindExistingFileInputs() {
        document.querySelectorAll('.attachment input[type="file"]').forEach((input) => {
            const rules = this.getRulesForInput(input);
            input.setAttribute('accept', rules.acceptedMimeTypes.join(','));
            this.bindFileChangeEvent(input);
        });
    },

    bindAddEvents() {
        document.querySelectorAll('.add-attachment').forEach((button) => {
            const container = button.previousElementSibling;
            if (container) {
                const template = container.querySelector('.attachment');
                if (template) {
                    container._attachmentTemplate = template.cloneNode(true);
                }
            }
            button.addEventListener('click', (e) => this.handleAddClick(button));
        });
    },

    bindRemoveEvents() {
        document.addEventListener('click', (e) => {
            if (e.target.closest('.remove-attachment')) {
                this.handleRemoveClick(e.target.closest('.remove-attachment'));
            }
        });
    },

    handleAddClick(button) {
        const attachmentsContainer = button.previousElementSibling;
        const attachmentLimit = parseInt(button.getAttribute('data-attachment-limit'), 10);
        const attachmentCount = attachmentsContainer.querySelectorAll('.attachment').length;

        if (attachmentCount >= attachmentLimit) return;

        const attachment = this.createAttachment(attachmentsContainer, attachmentCount + 1);
        if (attachment) {
            attachmentsContainer.appendChild(attachment);
        }

        if (attachmentCount + 1 === attachmentLimit) {
            button.style.display = 'none';
        }
    },

    handleRemoveClick(removeButton) {
        const attachment = removeButton.closest('.attachment');
        const attachmentsContainer = attachment.parentElement;
        const addButton = attachmentsContainer.nextElementSibling;
        const deletedAttachmentsInput = document.querySelector('input[name="details[deleted_attachments]"]');

        const input = attachment.querySelector('input');
        if (input && input.hasAttribute('data-attachment-id')) {
            this.deletedAttachmentIDs.push(parseInt(input.getAttribute('data-attachment-id')));
        }

        if (deletedAttachmentsInput) {
            deletedAttachmentsInput.value = JSON.stringify(this.deletedAttachmentIDs);
        }

        attachment.remove();
        this.updateAttachmentLabels(attachmentsContainer);

        if (
            attachmentsContainer.querySelectorAll('.attachment').length <
            parseInt(addButton.getAttribute('data-attachment-limit'), 10)
        ) {
            addButton.style.display = 'inline-block';
        }
    },

    createAttachment(container, index) {
        let attachment;
        const template = container.querySelector('.attachment');

        if (template) {
            attachment = template.cloneNode(true);
        } else if (container._attachmentTemplate) {
            attachment = container._attachmentTemplate.cloneNode(true);
        } else {
            console.error('No attachment template found');
            return null;
        }

        const oldInput = attachment.querySelector('input[type="file"]');
        const newInput = this.createFreshInput(oldInput, index);

        oldInput.parentNode.replaceChild(newInput, oldInput);
        this.updateAttachmentLabels(attachment, index);
        this.bindFileChangeEvent(newInput);

        return attachment;
    },

    createFreshInput(oldInput, index) {
        const newInput = document.createElement('input');
        newInput.setAttribute('type', 'file');
        newInput.setAttribute('name', oldInput.name.includes('[') ? `attachments[${index}]` : `attachment_${index}`);
        newInput.setAttribute('id', oldInput.name.includes('[') ? `attachments[${index}]` : `attachment_${index}`);
        newInput.setAttribute('data-attachment-id', `${index}`);
        newInput.className = oldInput.className;
        newInput.setAttribute('autocomplete', 'off');
        const rules = this.getRulesForInput(oldInput);
        newInput.setAttribute('accept', rules.acceptedMimeTypes.join(','));

        try {
            newInput.value = '';
        } catch (e) {
            // Ignore
        }

        return newInput;
    },

    updateAttachmentLabels(containerOrAttachment, index = null) {
        const attachments = containerOrAttachment.classList.contains('attachment')
            ? [containerOrAttachment]
            : containerOrAttachment.querySelectorAll('.attachment');

        attachments.forEach((attachment, i) => {
            const actualIndex = index || i + 1;
            const input = attachment.querySelector('input');
            const label = attachment.querySelector('label');

            if (input.name.includes('[')) {
                input.setAttribute('name', `attachments[${actualIndex}]`);
                input.setAttribute('id', `attachments[${actualIndex}]`);
            } else {
                input.setAttribute('name', `attachment_${actualIndex}`);
                input.setAttribute('id', `attachment_${actualIndex}`);
            }

            label.setAttribute('for', input.getAttribute('id'));
            label.textContent = 'Attachment ' + actualIndex;

            // Reset image preview
            const imageContainer = attachment.querySelector('.attachments-img');
            if (imageContainer) {
                const previewImage = imageContainer.querySelector('img');
                if (previewImage) {
                    previewImage.src = '/storage/installations/placeholder-image.jpg';
                }
            }
        });
    },

    bindFileChangeEvent(input) {
        input.addEventListener('change', (e) => {
            if (e.target.files.length > 0) {
                const file = e.target.files[0];
                const attachment = e.target.closest('.attachment');
                const label = attachment.querySelector('label');
                const imageContainer = attachment.querySelector('.attachments-img');

                const rules = this.getRulesForInput(e.target);

                const nameOk = rules.acceptedNamePattern.test(file.name || '');
                const typeOk = !file.type || rules.acceptedMimeTypes.includes(String(file.type).toLowerCase());
                const sizeOk = typeof file.size === 'number' && file.size <= rules.maxUploadBytes;

                if (!nameOk || !typeOk || !sizeOk) {
                    if (window.showAlert) {
                        const types = rules.acceptedMimeTypes.includes('image/webp')
                            ? 'JPG, PNG, or WebP'
                            : 'JPG or PNG';
                        window.showAlert(
                            'error',
                            `Upload a ${types} image up to ${this.formatBytes(rules.maxUploadBytes)}.`,
                        );
                    }

                    try {
                        e.target.value = '';
                    } catch (err) {
                        // Ignore
                    }

                    const name = e.target.getAttribute('name') || '';
                    const match = name.match(/(\d+)\]?$/);
                    const idx = match ? parseInt(match[1], 10) : null;
                    this.updateAttachmentLabels(attachment, Number.isFinite(idx) ? idx : null);
                    return;
                }

                label.textContent = file.name;
                const imageURL = URL.createObjectURL(file);

                if (imageContainer) {
                    const previewImage = imageContainer.querySelector('img');
                    if (previewImage) {
                        previewImage.src = imageURL;
                    }
                }
            }
        });
    },

    /**
     * Download single file
     */
    downloadFile(url, filename) {
        const link = document.createElement('a');
        link.href = url;
        if (typeof url === 'string' && (url.startsWith('blob:') || url.startsWith('data:'))) {
            link.download = filename || 'download';
        }
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    },

    /**
     * Download multiple files as ZIP
     */
    async downloadMultipleFilesAsZip(files) {
        if (!files || files.length === 0) {
            console.warn('No files provided for download');
            return;
        }

        try {
            const zip = new JSZip();

            for (const file of files) {
                const response = await fetch(file.url);
                const blob = await response.blob();
                zip.file(file.filename, blob);
            }

            const content = await zip.generateAsync({ type: 'blob' });
            saveAs(content, 'attachments.zip');
        } catch (error) {
            console.error('ZIP creation failed:', error);
        }
    },
};

// ============================================================================
// SUBMISSION MODAL MANAGEMENT
// ============================================================================

const SubmissionModalManager = {
    normalizeDataList(data) {
        if (Array.isArray(data)) return data;
        if (data && typeof data === 'object') return Object.values(data);
        return [];
    },

    getSubmissionsList() {
        return this.normalizeDataList(window.submissionsData);
    },

    getInstallationsList() {
        return this.normalizeDataList(window.installationsData);
    },

    findSubmission(submissionRef, installationRef) {
        const submissions = this.getSubmissionsList();
        const submissionRefString = submissionRef != null ? String(submissionRef) : '';
        const installationRefString = installationRef != null ? String(installationRef) : '';

        const strictMatch = submissions.find(
            (s) =>
                String(s?.reference ?? '') === submissionRefString &&
                String(s?.installation_reference ?? '') === installationRefString,
        );
        if (strictMatch) return strictMatch;

        const refOnlyMatch = submissions.find((s) => String(s?.reference ?? '') === submissionRefString);
        if (refOnlyMatch) return refOnlyMatch;

        return submissions.find((s) => String(s?.id ?? '') === submissionRefString) || null;
    },

    findInstallation(installationRef) {
        console.log('installationRef', installationRef);
        const installations = this.getInstallationsList();
        console.log('installations', installations);
        const installationRefString = installationRef != null ? String(installationRef) : '';
        return installations.find((i) => String(i?.reference ?? '') === installationRefString) || null;
    },

    openFromCard(card) {
        const installationRef = card?.dataset?.installationReference;
        const submissionRef = card?.dataset?.submissionReference;

        if (!submissionRef) {
            console.error('Missing submission reference');
            return;
        }

        const submission = this.findSubmission(submissionRef, installationRef);
        if (!submission) {
            console.error('Submission not found:', submissionRef);
            return;
        }
        console.log('submissionRef', submissionRef);
        console.log('submission', submission);

        const installation =
            this.findInstallation(installationRef) || this.findInstallation(submission.installation_reference) || null;

        if (submission.customer || submission.details) {
            this.openIOTM(submission, installation);
        } else {
            this.open(submission, installation);
        }
    },

    /**
     * Open submission modal with submission and installation data
     */
    open(submission, installation) {
        // Helper function to safely set element content
        function safeSetContent(id, content) {
            const element = document.getElementById(id);
            if (element) {
                element.textContent = content || '';
            }
        }

        // Populate modal fields
        safeSetContent('modal-title', submission.title);
        safeSetContent('modal-subtitle', submission.installers);
        safeSetContent('modal-installers', submission.installers);
        safeSetContent('modal-installation-date', this.formatDate(submission.created_at));
        safeSetContent('modal-door-style', submission.door_style);
        safeSetContent('modal-colour', submission.colour);
        safeSetContent('modal-glazing', submission.glazing);
        safeSetContent('modal-hardware', submission.hardware);
        safeSetContent('modal-description', submission.description);

        // Handle images
        this.handleImages(submission);

        // Show modal
        const modal = document.getElementById('submission-modal');
        if (modal) {
            modal.classList.remove('hidden');
            document.body.style.overflow = 'hidden';
        }
    },

    /**
     * Open submission modal with IOTM data structure
     */
    openIOTM(submission, installation) {
        if (!submission || typeof submission !== 'object') {
            console.error('Invalid submission object:', submission);
            return;
        }

        // Helper function to safely set element content
        function safeSetContent(id, content) {
            const element = document.getElementById(id);
            if (element) {
                element.textContent = content || '';
            }
        }

        // Helper function to safely toggle container visibility
        function safeToggleContainer(id, show) {
            const element = document.getElementById(id);
            if (element) {
                if (show) {
                    element.classList.remove('hidden');
                } else {
                    element.classList.add('hidden');
                }
            } else {
                console.warn('Element not found:', id);
            }
        }

        // Populate modal with submission data
        safeSetContent('modal-title', submission.title || 'Untitled');
        safeSetContent(
            'modal-subtitle',
            (submission.installers || '') +
                (submission.customer?.business_name ? ' of ' + submission.customer.business_name : ''),
        );
        safeSetContent('modal-installers', submission.installers || '');
        safeSetContent('modal-installation-date', this.formatDate(submission.installation_date));

        // Handle customer info
        if (submission.customer?.business_name) {
            safeSetContent('modal-customer', submission.customer.business_name);
            safeToggleContainer('modal-customer-row', true);
        } else {
            safeToggleContainer('modal-customer-row', false);
        }

        // Handle door style details
        if (submission.details?.door_style) {
            safeSetContent('modal-door-style', submission.details.door_style);
            safeToggleContainer('modal-door-style-row', true);
        } else {
            safeToggleContainer('modal-door-style-row', false);
        }

        // Handle colour details
        if (submission.details?.colour) {
            safeSetContent('modal-colour', submission.details.colour);
            safeToggleContainer('modal-colour-row', true);
        } else {
            safeToggleContainer('modal-colour-row', false);
        }

        // Handle glazing details
        if (submission.details?.glazing) {
            safeSetContent('modal-glazing', submission.details.glazing);
            safeToggleContainer('modal-glazing-row', true);
        } else {
            safeToggleContainer('modal-glazing-row', false);
        }

        // Handle hardware details
        if (submission.details?.hardware) {
            safeSetContent('modal-hardware', submission.details.hardware);
            safeToggleContainer('modal-hardware-row', true);
        } else {
            safeToggleContainer('modal-hardware-row', false);
        }

        // Handle images
        this.handleImagesIOTM(submission);

        // Update vote button state if function exists
        if (typeof this.updateVoteButton === 'function') {
            this.updateVoteButton(submission, installation);
        }

        // Show modal
        const modal = document.getElementById('submission-modal');
        if (modal) {
            modal.classList.remove('hidden');

            // Force display styles
            modal.style.display = 'block';
            modal.style.visibility = 'visible';
            modal.style.opacity = '1';

            document.body.style.overflow = 'hidden';
        }
    },

    updateVoteButton(submission, installation) {
        const voteBtn = document.getElementById('modal-vote-btn');
        const votedBadge = document.getElementById('modal-voted-badge');

        if (!voteBtn || !votedBadge) return;

        // Check if user has voted for this submission
        const hasVoted = window.userVotes && window.userVotes.includes(submission.reference);
        const globalVoted = window.globalUserHasVoted;

        // Determine if voting is closed
        // Check installation status and dates
        const now = new Date();
        const endDate = installation.voting_closes_at ? new Date(installation.voting_closes_at) : null;

        let isVotingClosed = false;
        if (installation.status === 'open') {
            isVotingClosed = true;
        } else if (installation.status === 'published') {
            isVotingClosed = true;
        } else if (endDate && now > endDate) {
            isVotingClosed = true;
        } else if (installation.status === 'closed' && !endDate) {
            isVotingClosed = true;
        }

        const isShortlistVotingOpen = installation.is_shortlisting_active && !isVotingClosed;
        const isVoteEligible = !isShortlistVotingOpen || submission.shortlisted;

        if (hasVoted) {
            // User voted for this specific submission
            voteBtn.classList.add('hidden');
            voteBtn.classList.remove('flex');
            votedBadge.classList.remove('hidden');
            votedBadge.classList.add('flex');
            votedBadge.classList.add('bg-secondary');
            votedBadge.innerHTML = `<i class="fas fa-thumbs-up"></i> Already voted`;
        } else if (globalVoted) {
            // User voted for another submission
            voteBtn.classList.add('hidden');
            voteBtn.classList.remove('flex');
            votedBadge.classList.remove('hidden');
            votedBadge.classList.add('flex');
            votedBadge.innerHTML = `<span class="text-gray-500">You voted for another submission</span>`;
            votedBadge.className =
                'hidden gap-2 items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg flex';
        } else if (!isVoteEligible) {
            voteBtn.classList.add('hidden');
            voteBtn.classList.remove('flex');
            votedBadge.classList.remove('hidden');
            votedBadge.classList.add('flex');
            votedBadge.innerHTML = `<span class="text-gray-500">Shortlisted only</span>`;
            votedBadge.className =
                'hidden gap-2 items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg flex';
        } else if (isVotingClosed) {
            // Voting is closed
            voteBtn.classList.add('hidden');
            voteBtn.classList.remove('flex');
            votedBadge.classList.remove('hidden');
            votedBadge.classList.add('flex');
            votedBadge.innerHTML = `<span class="text-gray-500">Voting Closed</span>`;
            votedBadge.className =
                'hidden gap-2 items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg flex';
        } else {
            // User can vote
            voteBtn.classList.remove('hidden');
            voteBtn.classList.add('flex');
            votedBadge.classList.add('hidden');
            votedBadge.classList.remove('flex');

            const selectedRef =
                window.InstallationVoting?.selectedSubmission?.submissionReference ||
                document.querySelector('input[name="votes[]"]')?.value ||
                document.querySelector('input[name="votes\\[\\]"]')?.value ||
                document.querySelector('input[name="votes[]"]')?.getAttribute('value') ||
                null;
            const isSelected = selectedRef === submission.reference;

            voteBtn.disabled = isSelected;
            voteBtn.innerHTML = isSelected
                ? '<i class="fa-solid fa-check"></i> Selected'
                : '<i class="fa-solid fa-thumbs-up"></i> Select';

            // Attach click handler (select only)
            voteBtn.onclick = (e) => {
                e.stopPropagation();
                const voteButtonOnCard = document.querySelector(
                    `.installation-door-card-like[data-installation-reference="${installation.reference}"][data-submission-reference="${submission.reference}"]`,
                );
                if (voteButtonOnCard && window.SubmissionModalManager) {
                    window.SubmissionModalManager.handleVoteSelection(voteButtonOnCard);
                } else if (window.InstallationVoting) {
                    const mockBtn = document.createElement('button');
                    mockBtn.dataset.installationReference = installation.reference;
                    mockBtn.dataset.submissionReference = submission.reference;
                    window.InstallationVoting.selectSubmission(mockBtn);
                }

                const modal = document.getElementById('submission-modal');
                if (modal) {
                    modal.classList.add('hidden');
                    modal.style.display = '';
                }
                document.body.style.overflow = '';
            };
        }
    },

    /**
     * Handle images for submission modal
     */
    handleImages(submission) {
        const images = submission.images || [];
        console.log('Handling images for submission:', submission.title, 'images:', images);

        // Set main image to first image
        if (images.length > 0) {
            const mainImage = document.getElementById('modal-main-image');
            if (mainImage) {
                mainImage.src = images[0].url;
                mainImage.alt = submission.title;
            }
        }

        // Create thumbnail gallery
        const thumbnailsContainer = document.getElementById('modal-thumbnails');
        if (thumbnailsContainer) {
            thumbnailsContainer.innerHTML = '';

            images.forEach((image, index) => {
                const thumb = document.createElement('div');
                thumb.className =
                    'overflow-hidden rounded-lg border-2 border-transparent transition-colors cursor-pointer hover:border-primary';
                thumb.innerHTML = `<img src="${image.url}" alt="${submission.title} thumbnail ${index + 1}" class="object-cover w-full h-full">`;

                thumb.addEventListener('click', () => {
                    const mainImage = document.getElementById('modal-main-image');
                    if (mainImage) {
                        mainImage.src = image.url;
                    }

                    // Update active thumbnail
                    thumbnailsContainer.querySelectorAll('div').forEach((div) => {
                        div.classList.remove('border-primary');
                        div.classList.add('border-transparent');
                    });
                    thumb.classList.remove('border-transparent');
                    thumb.classList.add('border-primary');
                });

                thumbnailsContainer.appendChild(thumb);
            });
        }
    },

    /**
     * Handle images for IOTM submission modal
     */
    handleImagesIOTM(submission) {
        const images = submission.images || [];
        console.log('Handling IOTM images for submission:', submission.title, 'images:', images);

        const mainImage = document.getElementById('modal-main-image');
        const noImage = document.getElementById('modal-no-image');
        const thumbnailsContainer = document.getElementById('modal-thumbnails');

        if (!mainImage || !noImage || !thumbnailsContainer) {
            console.error('Modal image elements not found');
            return;
        }

        if (images.length > 0) {
            // Use the URL from the image object
            const firstImage = images[0];
            const imageUrl = firstImage && firstImage.url ? firstImage.url : null;

            if (imageUrl) {
                mainImage.src = imageUrl;
                mainImage.alt = submission.title;
                mainImage.classList.remove('hidden');
                noImage.classList.add('hidden');
            } else {
                mainImage.classList.add('hidden');
                noImage.classList.remove('hidden');
            }

            // Handle thumbnails for multiple images
            if (images.length > 1) {
                thumbnailsContainer.innerHTML = '';
                thumbnailsContainer.classList.remove('hidden');

                images.forEach((image, index) => {
                    const thumbnailUrl = image && image.url ? image.url : null;
                    if (thumbnailUrl) {
                        const thumbnailDiv = document.createElement('div');
                        thumbnailDiv.className = 'overflow-hidden rounded-lg cursor-pointer aspect-w-1 aspect-h-1';
                        thumbnailDiv.innerHTML = `
                            <img src="${thumbnailUrl.replace(/'/g, "\\'")}"
                                 alt="${(submission.title || '').replace(/'/g, "\\'")} - Image ${index + 1}"
                                 class="object-cover w-full h-full transition-opacity hover:opacity-80"
                                 data-index="${index}">
                        `;
                        thumbnailDiv.addEventListener('click', () => {
                            SubmissionModalManager.setMainImage(
                                thumbnailUrl,
                                `${submission.title} - Image ${index + 1}`,
                            );
                        });
                        thumbnailsContainer.appendChild(thumbnailDiv);
                    }
                });
            } else {
                thumbnailsContainer.classList.add('hidden');
            }
        } else {
            console.log('No images found, showing placeholder');
            mainImage.classList.add('hidden');
            noImage.classList.remove('hidden');
            thumbnailsContainer.classList.add('hidden');
        }
    },

    /**
     * Set main image in modal
     */
    setMainImage(url, alt) {
        const mainImage = document.getElementById('modal-main-image');
        const noImage = document.getElementById('modal-no-image');

        if (mainImage) {
            mainImage.src = url;
            if (alt) mainImage.alt = alt;
            mainImage.classList.remove('hidden');
        }

        if (noImage) {
            noImage.classList.add('hidden');
        }
    },

    /**
     * Close submission modal
     */
    close() {
        const modal = document.getElementById('submission-modal');
        if (modal) {
            modal.classList.add('hidden');
            document.body.style.overflow = '';

            // Clear any inline styles that might force visibility
            modal.style.display = '';
            modal.style.visibility = '';
            modal.style.opacity = '';
        }
    },

    /**
     * Handle vote selection (for card clicks)
     */
    handleVoteSelection(button) {
        const e = window.event;
        if (e && typeof e.stopPropagation === 'function') {
            e.stopPropagation();
        }

        const installationRef = button.dataset.installationReference;
        const submissionRef = button.dataset.submissionReference;
        if (!installationRef || !submissionRef) {
            return;
        }

        const voteForm = document.querySelector('[id^="vote-form-"]');
        const submitVoteBtn = voteForm?.querySelector('[id^="submit-vote-btn-"]');
        if (voteForm && submitVoteBtn) {
            document.querySelectorAll('.installation-door-card-like').forEach((btn) => {
                btn.classList.remove('bg-primary', 'text-white');
                btn.classList.add('bg-gray-50', 'text-gray-400');
                btn.innerHTML = '<i class="fa-solid fa-thumbs-up"></i> Vote';
            });

            button.classList.remove('bg-gray-50', 'text-gray-400');
            button.classList.add('bg-primary', 'text-white');
            button.innerHTML = '<i class="fa-solid fa-check"></i> Selected';

            let votesInput = voteForm.querySelector('input[name="votes[]"]');
            if (!votesInput) {
                votesInput = document.createElement('input');
                votesInput.type = 'hidden';
                votesInput.name = 'votes[]';
                voteForm.appendChild(votesInput);
            }
            votesInput.value = submissionRef;

            const selectedSubmissionsContainer = document.querySelector('[id^="selected-submissions-"]');
            if (selectedSubmissionsContainer) {
                const submission = this.findSubmission(submissionRef, installationRef);
                selectedSubmissionsContainer.innerHTML = `
                    <div class="inline-flex gap-2 items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 rounded-lg">
                        <i class="fa-solid fa-check text-primary"></i>
                        <span>${submission?.title || 'Submission selected'}</span>
                    </div>
                `;
            }

            submitVoteBtn.disabled = false;

            // Also enable any iotm-submit-vote-btn buttons (inline + sticky)
            document.querySelectorAll('.iotm-submit-vote-btn').forEach(function (btn) {
                btn.disabled = false;
            });

            // Show and slide-in the sticky bottom submit button
            const stickyWrapper = document.getElementById('iotm-sticky-vote-btn');
            if (stickyWrapper) {
                stickyWrapper.classList.remove('hidden');
                stickyWrapper.classList.remove('translate-y-full');
                stickyWrapper.classList.add('translate-y-0');
            }

            // Store selection in InstallationVoting so submitVote() can use it
            if (window.InstallationVoting) {
                window.InstallationVoting.selectedSubmission = {
                    installationReference: installationRef,
                    submissionReference: submissionRef,
                };
            }

            if (!voteForm.dataset.voteSubmitHandlerAttached) {
                voteForm.dataset.voteSubmitHandlerAttached = 'true';
                voteForm.addEventListener('submit', (event) => {
                    event.preventDefault();

                    if (submitVoteBtn.disabled) {
                        return;
                    }

                    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || '';
                    const originalHtml = submitVoteBtn.innerHTML;
                    submitVoteBtn.disabled = true;
                    submitVoteBtn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Submitting...';

                    fetch('/installation/' + installationRef + '/vote', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-CSRF-TOKEN': csrfToken,
                        },
                        body: JSON.stringify({
                            votes: [votesInput.value],
                        }),
                    })
                        .then(async (response) => {
                            const data = await response.json().catch(() => ({}));
                            if (!response.ok) {
                                throw new Error(data.message || 'Error submitting vote');
                            }
                            return data;
                        })
                        .then(() => {
                            if (Array.isArray(window.userVotes) && !window.userVotes.includes(votesInput.value)) {
                                window.userVotes.push(votesInput.value);
                            }
                            window.globalUserHasVoted = true;

                            const selectedCard = document.querySelector(
                                `[data-installation-reference="${installationRef}"][data-submission-reference="${votesInput.value}"]`,
                            );
                            if (selectedCard) {
                                const voteContainer =
                                    selectedCard.querySelector('.installation-door-card-like')?.parentElement;
                                if (voteContainer) {
                                    voteContainer.innerHTML =
                                        '<span class="px-2 py-1 text-xs font-semibold whitespace-nowrap rounded-full text-primary bg-primary/10">Voted ✓</span>';
                                }
                            }

                            if (window.showAlert) {
                                window.showAlert('success', 'Thank you for voting!');
                            }

                            submitVoteBtn.innerHTML = '<i class="fas fa-check"></i> Vote Submitted';

                            // Hide inline vote prompts and sticky button after successful vote
                            document.querySelectorAll('.iotm-vote-prompt').forEach(function (el) {
                                el.classList.add('hidden');
                            });
                            const stickyBtnEl = document.getElementById('iotm-sticky-vote-btn');
                            if (stickyBtnEl) {
                                stickyBtnEl.classList.add('hidden');
                            }
                        })
                        .catch((err) => {
                            submitVoteBtn.disabled = false;
                            submitVoteBtn.innerHTML = originalHtml;
                            if (window.showAlert) {
                                window.showAlert('error', err.message || 'Error submitting vote');
                            }
                        });
                });
            }

            return;
        }

        // Use the InstallationVoting system if available
        if (window.InstallationVoting) {
            window.InstallationVoting.selectSubmission(button);
            return;
        }

        // Fallback to original logic
        // Remove selection from all cards
        document.querySelectorAll('.installation-door-card').forEach((card) => {
            card.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
            card.classList.add('ring-0');
        });

        // Add selection to clicked card
        const card = button.closest('.installation-door-card');
        if (card) {
            card.classList.remove('ring-0');
            card.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
        }

        // Find the installation context and enable the corresponding submit button
        // Look for the submit button in the same container as the vote form
        const container = button.closest('.container') || document;
        const submitBtn = container.querySelector('[id^="submit-vote-btn-"]');
        if (submitBtn) {
            submitBtn.disabled = false;
            return;
        }

        if (button.disabled) {
            return;
        }

        button.disabled = true;

        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || '';
        fetch('/installation/' + installationRef + '/vote', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrfToken,
            },
            body: JSON.stringify({
                votes: [submissionRef],
            }),
        })
            .then(async (response) => {
                const data = await response.json().catch(() => ({}));
                if (!response.ok) {
                    throw new Error(data.message || 'Error submitting vote');
                }
                return data;
            })
            .then(() => {
                if (Array.isArray(window.userVotes) && !window.userVotes.includes(submissionRef)) {
                    window.userVotes.push(submissionRef);
                }
                window.globalUserHasVoted = true;

                const selectedCard = document.querySelector(
                    `[data-installation-reference="${installationRef}"][data-submission-reference="${submissionRef}"]`,
                );
                if (selectedCard) {
                    const voteContainer = selectedCard.querySelector('.installation-door-card-like')?.parentElement;
                    if (voteContainer) {
                        voteContainer.innerHTML =
                            '<span class="px-2 py-1 text-xs font-semibold whitespace-nowrap rounded-full text-primary bg-primary/10">Voted ✓</span>';
                    }
                }

                if (window.showAlert) {
                    window.showAlert('success', 'Thank you for voting!');
                }
            })
            .catch((err) => {
                button.disabled = false;
                if (window.showAlert) {
                    window.showAlert('error', err.message || 'Error submitting vote');
                }
            });
    },

    /**
     * Format date for display
     */
    formatDate(dateString) {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    },

    /**
     * Open modal image in new tab
     */
    openImageInNewTab() {
        const mainImage = document.getElementById('modal-main-image');
        if (mainImage && mainImage.src) {
            window.open(mainImage.src, '_blank');
        }
    },
};

// ============================================================================
// ALERT MANAGEMENT
// ============================================================================

const AlertManager = {
    showAlert(type, message) {
        const alertClass = type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
        const icon =
            type === 'success'
                ? '<svg class="mr-2 w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path></svg>'
                : '<svg class="mr-2 w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path></svg>';

        const alertHtml = `
            <div class="fixed top-4 right-4 z-50 transition-opacity duration-300 ease-in-out opacity-0 transform translate-y-[-1rem]" id="toast-alert">
                <div class="flex items-center px-4 py-3 rounded shadow-lg ${alertClass}">
                    ${icon}
                    <span>${message}</span>
                </div>
            </div>
        `;

        document.body.insertAdjacentHTML('beforeend', alertHtml);
        const alertElement = document.getElementById('toast-alert');

        // Trigger reflow
        void alertElement.offsetWidth;

        // Show alert
        alertElement.classList.remove('opacity-0', 'translate-y-[-1rem]');

        // Hide and remove after delay
        setTimeout(() => {
            alertElement.classList.add('opacity-0', 'translate-y-[-1rem]');
            setTimeout(() => alertElement.remove(), 300);
        }, 3000);
    },
};

// ============================================================================
// CAROUSEL MANAGEMENT
// ============================================================================

const CarouselManager = {
    init() {
        // Initialize submission carousels
        document.querySelectorAll('.submission-carousel').forEach(function (carousel) {
            // Check for sliding track implementation (responsive multi-item)
            const track = carousel.querySelector('.carousel-track');
            if (track) {
                const indicators = carousel.querySelectorAll('.carousel-indicator');
                const instId = carousel.dataset.installationId;

                function getVisible() {
                    const w = window.innerWidth;
                    if (w >= 1024) return 4;
                    if (w >= 768) return 3;
                    if (w >= 640) return 2;
                    return 1;
                }

                const originalHtmlKey = '__amicusOriginalCarouselHtml';
                const initKey = '__amicusInfiniteCarouselInit';
                if (!carousel[originalHtmlKey]) {
                    carousel[originalHtmlKey] = track.innerHTML;
                }

                if (!carousel[initKey]) {
                    const outerController = new AbortController();
                    let setupController = null;
                    let resizeTimer = null;
                    let slideWidthPx = 0;

                    function setTransform(index, visible, animated) {
                        if (!animated) {
                            track.style.transition = 'none';
                        } else if (track.style.transition === 'none') {
                            track.style.transition = '';
                        }

                        if (slideWidthPx > 0) {
                            const x = Math.round(slideWidthPx * index);
                            track.style.transform = `translate3d(-${x}px, 0, 0)`;
                        } else {
                            const pct = (100 / visible) * index;
                            track.style.transform = `translate3d(-${pct}%, 0, 0)`;
                        }

                        if (!animated) {
                            requestAnimationFrame(() => {
                                track.style.transition = '';
                            });
                        }
                    }

                    function updateDots(logicalIndex) {
                        if (!indicators.length) return;
                        const idx = Math.max(0, Math.min(logicalIndex, indicators.length - 1));
                        indicators.forEach((dot, i) => {
                            dot.classList.toggle('bg-primary', i === idx);
                            dot.classList.toggle('bg-gray-300', i !== idx);
                        });
                    }

                    function setup() {
                        if (setupController) {
                            setupController.abort();
                        }
                        setupController = new AbortController();
                        const signal = setupController.signal;

                        track.innerHTML = carousel[originalHtmlKey];
                        const originals = Array.from(track.querySelectorAll('.flex-shrink-0'));
                        const total = originals.length;
                        const visible = getVisible();

                        if (!total) return;

                        if (total <= visible) {
                            updateDots(0);
                            return;
                        }

                        const getLoopIndex = (idx) => {
                            const mod = idx % total;
                            return mod < 0 ? mod + total : mod;
                        };

                        const cloneAt = (idx) => originals[getLoopIndex(idx)].cloneNode(true);

                        const before = Array.from({ length: visible }, (_, i) => cloneAt(total - visible + i));
                        const after = Array.from({ length: visible }, (_, i) => cloneAt(i));

                        track.innerHTML = '';
                        before.forEach((n) => track.appendChild(n));
                        originals.forEach((n) => track.appendChild(n));
                        after.forEach((n) => track.appendChild(n));

                        slideWidthPx = track.querySelector('.flex-shrink-0')?.getBoundingClientRect().width || 0;

                        let current = visible;
                        let isTransitioning = false;
                        let settleTimer = null;

                        function logicalIndex() {
                            const raw = (current - visible) % total;
                            return raw < 0 ? raw + total : raw;
                        }

                        function snapIfNeeded() {
                            if (current >= total + visible || current < visible) {
                                const normalized = ((((current - visible) % total) + total) % total) + visible;
                                current = normalized;
                                setTransform(current, visible, false);
                                updateDots(logicalIndex());
                            }
                        }

                        function go(nextIndex, animated = true) {
                            if (signal.aborted) return;
                            if (animated && isTransitioning) return;
                            current = nextIndex;
                            setTransform(current, visible, animated);
                            updateDots(logicalIndex());

                            if (animated) {
                                isTransitioning = true;
                                if (settleTimer) window.clearTimeout(settleTimer);
                                settleTimer = window.setTimeout(() => {
                                    if (signal.aborted) return;
                                    isTransitioning = false;
                                    requestAnimationFrame(() => snapIfNeeded());
                                }, 650);
                            }
                        }

                        go(current, false);

                        track.addEventListener(
                            'transitionend',
                            (e) => {
                                if (e.target !== track) return;
                                if (e.propertyName && e.propertyName !== 'transform') return;
                                if (signal.aborted) return;
                                isTransitioning = false;
                                if (settleTimer) window.clearTimeout(settleTimer);
                                requestAnimationFrame(() => snapIfNeeded());
                            },
                            { signal },
                        );

                        document
                            .querySelectorAll(`.carousel-control[data-installation-id="${instId}"]`)
                            .forEach((btn) => {
                                btn.addEventListener(
                                    'click',
                                    () => {
                                        const dir = parseInt(btn.dataset.direction);
                                        if (Number.isNaN(dir)) return;
                                        go(current + dir, true);
                                    },
                                    { signal },
                                );
                            });

                        indicators.forEach((dot) => {
                            dot.addEventListener(
                                'click',
                                () => {
                                    const idx = parseInt(dot.dataset.slideIndex);
                                    if (Number.isNaN(idx)) return;
                                    go(visible + idx, true);
                                },
                                { signal },
                            );
                        });
                    }

                    window.addEventListener(
                        'resize',
                        () => {
                            if (resizeTimer) window.clearTimeout(resizeTimer);
                            resizeTimer = window.setTimeout(() => setup(), 120);
                        },
                        { signal: outerController.signal },
                    );

                    setup();
                    carousel[initKey] = true;
                }

                return;
            }

            const items = carousel.querySelectorAll('.carousel-item');
            const indicators = carousel.parentElement.querySelectorAll('.carousel-indicator');
            const prevBtn = carousel.parentElement.querySelector('.carousel-prev');
            const nextBtn = carousel.parentElement.querySelector('.carousel-next');
            let current = 0;

            if (!items.length) return;

            function show(index) {
                items.forEach((item) => item.classList.add('hidden'));
                items[index].classList.remove('hidden');

                if (indicators.length) {
                    indicators.forEach((ind) => ind.classList.remove('bg-primary', 'bg-gray-300'));
                    indicators.forEach((ind) => ind.classList.add('bg-gray-300'));
                    indicators[index].classList.remove('bg-gray-300');
                    indicators[index].classList.add('bg-primary');
                }
            }

            function next() {
                current = (current + 1) % items.length;
                show(current);
            }

            function prev() {
                current = (current - 1 + items.length) % items.length;
                show(current);
            }

            function go(index) {
                current = index;
                show(current);
            }

            if (prevBtn) prevBtn.addEventListener('click', prev);
            if (nextBtn) nextBtn.addEventListener('click', next);

            if (indicators.length) {
                indicators.forEach((ind, i) => {
                    ind.addEventListener('click', () => go(i));
                });
            }

            show(0);
        });

        CarouselManager.initIOTMCarousel();
    },

    initIOTMCarousel() {
        const carousel = document.querySelector('.iotm-carousel');
        if (!carousel) return;

        const container = carousel.querySelector('.iotm-carousel-container');
        const items = carousel.querySelectorAll('.iotm-carousel-item');
        const prevBtn = carousel.querySelector('.iotm-prev');
        const nextBtn = carousel.querySelector('.iotm-next');
        const dots = carousel.querySelectorAll('.iotm-dot');
        let current = 0;

        if (!items.length) return;

        function update() {
            const offset = -current * 100;
            container.style.transform = `translateX(${offset}%)`;

            dots.forEach((dot, index) => {
                if (index === current) {
                    dot.classList.add('bg-primary', 'w-8');
                    dot.classList.remove('bg-gray-300', 'w-2');
                } else {
                    dot.classList.remove('bg-primary', 'w-8');
                    dot.classList.add('bg-gray-300', 'w-2');
                }
            });
        }

        function go(index) {
            current = index;
            update();
        }

        if (prevBtn) {
            prevBtn.addEventListener('click', () => {
                current = (current - 1 + items.length) % items.length;
                update();
            });
        }

        if (nextBtn) {
            nextBtn.addEventListener('click', () => {
                current = (current + 1) % items.length;
                update();
            });
        }

        dots.forEach((dot, index) => {
            dot.addEventListener('click', () => go(index));
        });

        // Touch support
        let touchStartX = 0;
        let touchEndX = 0;

        carousel.addEventListener(
            'touchstart',
            (e) => {
                touchStartX = e.changedTouches[0].screenX;
            },
            { passive: true },
        );

        carousel.addEventListener(
            'touchend',
            (e) => {
                touchEndX = e.changedTouches[0].screenX;
                handleSwipe();
            },
            { passive: true },
        );

        function handleSwipe() {
            if (touchEndX < touchStartX - 50) {
                // Swipe left
                current = (current + 1) % items.length;
                update();
            }
            if (touchEndX > touchStartX + 50) {
                // Swipe right
                current = (current - 1 + items.length) % items.length;
                update();
            }
        }

        // Handle resize
        window.addEventListener('resize', () => go(current));
        go(0);
    },
};

// ============================================================================
// POLL VOTING MANAGEMENT
// ============================================================================

const PollManager = {
    init() {
        console.log('PollManager initialized');
        this.initVoting();
        this.initImageModals();
    },

    initVoting() {
        // Use class .vote-form or ID #vote-form
        const forms = document.querySelectorAll('.vote-form, #vote-form');
        forms.forEach((form) => {
            const submitButton = form.querySelector('button[type="submit"]');
            const hasVotedMessage =
                form.querySelector('#vote-submitted-message') || document.getElementById('vote-submitted-message');
            const hasVotedError =
                form.querySelector('#vote-submitted-error') || document.getElementById('vote-submitted-error');
            const voteInputs = form.querySelectorAll('.vote-input');

            // Handle vote input selection
            voteInputs.forEach((input) => {
                input.addEventListener('click', function () {
                    voteInputs.forEach((inp) => (inp.checked = false));
                    this.checked = true;
                });
            });

            // Handle form submission
            form.addEventListener('submit', function (event) {
                event.preventDefault();
                const selectedOption = form.querySelector('input[name="vote"]:checked');

                if (!selectedOption) {
                    // Show error message when no option is selected
                    if (hasVotedError) {
                        hasVotedError.innerHTML = `
                            <p class="text-red-8text-red-800                         <i class="mx-2 fa-somx-2 fa-solid fa-xmark                             Please select an option before submitting your vote.
                            </p>
                        `;
                        hasVotedError.classList.remove('hidden');
                        setTimeout(() => hasVotedError.classList.add('hidden'), 4000);
                    }
                    return;
                }

                // Get the poll reference from form data or URL
                let pollReference = form.dataset.pollReference;
                let url = form.action;

                // If no action URL or dataset reference, try to construct from current URL
                if (!url || url === window.location.href || url === '') {
                    pollReference = pollReference || window.location.pathname.split('/').pop();
                    if (pollReference) {
                        url = `/polls/${pollReference}/vote`;
                    } else {
                        console.error('No poll reference found - cannot construct voting URL');
                        return;
                    }
                }

                // Add loading state
                const originalText = submitButton.innerHTML;
                submitButton.innerHTML = '<span class="animate-puanimate-pulseng...</span>';
                submitButton.disabled = true;
                axios
                    .post(url, {
                        option: selectedOption.dataset.ref,
                        _token: getCsrfToken(),
                    })
                    .then((response) => {
                        const stats = response.data.stats;

                        if (hasVotedMessage) {
                            hasVotedMessage.classList.remove('hidden');
                        }

                        // Update vote counts for each option
                        if (stats) {
                            stats.forEach((stat) => {
                                const inputOption = form.querySelector(`input[data-ref="${stat.reference}"]`);
                                if (inputOption) {
                                    inputOption.dataset.voteCount = stat.votes;
                                }
                            });
                        }

                        // Reload page after delay
                        setTimeout(() => {
                            if (hasVotedMessage) {
                                hasVotedMessage.classList.add('hidden');
                            }
                            location.reload();
                        }, 2000);
                    })
                    .catch((error) => {
                        console.error('Vote submission error:', error);

                        // Show error message
                        if (hasVotedError) {
                            let errorMessage = 'An error occurred while submitting your vote.';

                            if (error.response && error.response.status === 403) {
                                errorMessage = 'You have already voted in this poll.';
                            } else if (error.response && error.response.data && error.response.data.message) {
                                errorMessage = error.response.data.message;
                            }

                            hasVotedError.innerHTML = `<p class="text-red-8text-red-800essage}</p>`;
                            hasVotedError.classList.remove('hidden');

                            setTimeout(() => {
                                hasVotedError.classList.add('hidden');
                            }, 4000);
                        }

                        // Reset button
                        submitButton.innerHTML = originalText;
                        submitButton.disabled = false;
                    });
            });
        });
    },

    initImageModals() {
        // Create modal HTML if it doesn't exist
        if (!document.getElementById('poll-image-modal')) {
            const modalHTML = `
                <div id="poll-image-modal" class="poll-image-modal">
                    <div class="poll-image-modal-content">
                        <div class="poll-image-modal-close">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                        </div>
                        <img id="poll-modal-image" src="" alt="">
                        <div id="poll-modal-caption" class="poll-image-modal-caption"></div>
                    </div>
                </div>
            `;
            document.body.insertAdjacentHTML('beforeend', modalHTML);
        }

        const modal = document.getElementById('poll-image-modal');
        const modalImage = document.getElementById('poll-modal-image');
        const modalCaption = document.getElementById('poll-modal-caption');
        const modalClose = modal.querySelector('.poll-image-modal-close');

        // Function to open modal
        function openModal(imageSrc, caption) {
            modalImage.src = imageSrc;
            modalCaption.textContent = caption;
            modal.classList.add('active');
            document.body.style.overflow = 'hidden';
        }

        // Function to close modal
        function closeModal() {
            modal.classList.remove('active');
            document.body.style.overflow = '';
            // Clear image after animation to prevent flash
            setTimeout(() => {
                modalImage.src = '';
                modalCaption.textContent = '';
            }, 300);
        }

        // Event delegation: handle clicks on thumbnail or overlay
        document.addEventListener('click', function (e) {
            const imgTarget = e.target.closest('.poll-option-image');
            const overlayTarget = e.target.closest('.poll-image-overlay');
            if (imgTarget || overlayTarget) {
                e.preventDefault();
                e.stopPropagation();
                const container = (imgTarget || overlayTarget).closest('.poll-image-container');
                const img = container ? container.querySelector('.poll-option-image') : imgTarget;
                if (img) {
                    const imageSrc = img.dataset.fullImage;
                    const caption = img.dataset.caption || '';
                    if (imageSrc) {
                        openModal(imageSrc, caption);
                    }
                }
            }
        });

        // Add click event to overlay and close button
        modal.addEventListener('click', function (e) {
            if (e.target === modal || e.target === modalClose) {
                closeModal();
            }
        });

        // Close on Escape key
        document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape' && modal.classList.contains('active')) {
                closeModal();
            }
        });

        // View Gallery button functionality
        const viewGalleryBtn = document.getElementById('view-poll-gallery');
        if (viewGalleryBtn) {
            viewGalleryBtn.addEventListener('click', function (e) {
                e.preventDefault();

                const firstImage = document.querySelector('.poll-option-image');
                if (firstImage) {
                    firstImage.click();
                }
            });
        }
    },
};

// ============================================================================
// GLOBAL ALERT MANAGER
// ============================================================================

const GlobalAlertManager = {
    showAlert(message, type = 'info') {
        const alert = document.getElementById('iotm-alert');
        const alertInner = document.getElementById('iotm-alert-inner');
        const alertIcon = document.getElementById('iotm-alert-icon');
        const alertMsg = document.getElementById('iotm-alert-msg');

        if (!alert || !alertInner || !alertIcon || !alertMsg) {
            console.warn('Alert elements not found');
            return;
        }

        // Set message
        alertMsg.textContent = message;

        // Set styles based on type
        const styles = {
            success: {
                inner: 'bg-green-50 border border-green-200',
                icon: '<svg class="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>',
            },
            error: {
                inner: 'w-5 h-5 text-green-600border-red-200',
                icon: '<svg class="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>',
            },
            info: {
                inner: 'w-5 h-5 text-red-600der border-blue-200',
                icon: '<svg class="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>',
            },
            warning: {
                inner: 'w-5 h-5 text-blue-600yellow-50 border border-yellow-200',
                icon: '<svg class="w-5 h-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-w-5 h-5 text-yellow-600.5z"/></svg>',
            },
        };

        const style = styles[type] || styles.info;
        alertInner.className = `flex gap-3 items-start p-4 rounded-xl shadow-xl ${style.inner}`;
        alertIcon.innerHTML = style.icon;

        // Show alert
        alert.classList.remove('hidden');

        // Auto-hide after 5 seconds
        setTimeout(() => {
            alert.classList.add('hidden');
        }, 5000);
    },
};

// ============================================================================
// GLOBAL CAROUSEL MANAGER
// ============================================================================

const GlobalCarouselManager = {
    init() {
        // Initialize submission carousels
        document.querySelectorAll('.submission-carousel').forEach(function (carousel) {
            const track = carousel.querySelector('.carousel-track');
            const indicators = carousel.querySelectorAll('.carousel-indicator');
            const instId = carousel.dataset.installationId;
            if (!track) return;

            const items = track.querySelectorAll('.flex-shrink-0');
            const total = items.length;
            let current = 0;

            function getVisible() {
                const w = window.innerWidth;
                if (w >= 1024) return 4;
                if (w >= 768) return 3;
                if (w >= 640) return 2;
                return 1;
            }

            function go(index) {
                const visible = getVisible();
                const max = Math.max(0, total - visible);
                current = Math.max(0, Math.min(index, max));
                const pct = (100 / visible) * current;
                track.style.transform = `translateX(-${pct}%)`;
                indicators.forEach((dot, i) => {
                    dot.classList.toggle('bg-primary', i === current);
                    dot.classList.toggle('bg-gray-300', i !== current);
                });
            }

            // Controls
            document.querySelectorAll(`.carousel-control[data-installation-id="${instId}"]`).forEach((btn) => {
                btn.addEventListener('click', () => go(current + parseInt(btn.dataset.direction)));
            });

            // Indicators
            indicators.forEach((dot, i) => {
                dot.addEventListener('click', () => go(i));
            });

            window.addEventListener('resize', () => go(current));
            go(0);
        });

        // Initialize IOTM carousel
        CarouselManager.initIOTMCarousel();
    },

    initIOTMCarousel() {
        const track = document.getElementById('iotm-track');
        const prevBtn = document.getElementById('iotm-prev');
        const nextBtn = document.getElementById('iotm-next');
        const dots = document.querySelectorAll('.iotm-dot');
        if (!track) return;

        const items = track.querySelectorAll('.flex-shrink-0');
        const total = items.length;
        let current = 0;

        function getVisible() {
            const w = window.innerWidth;
            if (w >= 1024) return 4;
            if (w >= 768) return 3;
            if (w >= 640) return 2;
            return 1;
        }

        function go(index) {
            const visible = getVisible();
            const max = Math.max(0, total - visible);
            current = Math.max(0, Math.min(index, max));
            const pct = (100 / visible) * current;
            track.style.transform = `translateX(-${pct}%)`;
            dots.forEach((d, i) => {
                d.classList.toggle('bg-primary', i === current);
                d.classList.toggle('bg-gray-300', i !== current);
            });
        }

        prevBtn?.addEventListener('click', () => go(current - 1));
        nextBtn?.addEventListener('click', () => go(current + 1));
        dots.forEach((d) => d.addEventListener('click', () => go(parseInt(d.dataset.index))));
        window.addEventListener('resize', () => go(current));
        go(0);
    },
};

// ============================================================================
// GALLERY MANAGEMENT
// ============================================================================

const GalleryManager = {
    initialized: false,
    btn: null,
    selectAllBtn: null,
    mediaIdsInput: null,
    form: null,

    init() {
        if (this.initialized) return;

        this.btn = document.getElementById('selected-gallery-download');
        this.selectAllBtn = document.getElementById('select-all-gallery');
        this.mediaIdsInput = document.getElementById('download-media-ids');
        this.form = document.getElementById('download-selected-form');

        // Only initialize if we're on a gallery page with these elements
        // We check for selectAllBtn or btn because sometimes one might exist without the other
        // But usually they go together. If neither exists, we don't need to do anything.
        if (!this.btn && !this.selectAllBtn && document.querySelectorAll('.fake-checkbox.gallery-item').length === 0)
            return;

        this.bindEvents();
        this.initialized = true;

        // Initialize state for any pre-checked boxes (browser back button, etc)
        this.updateDownloadButton();
        document.querySelectorAll('.fake-checkbox.gallery-item input[type="checkbox"]').forEach((cb) => {
            if (cb.checked) {
                this.updateCheckboxState(cb);
            }
        });
    },

    bindEvents() {
        // Delegate change events for checkboxes
        document.addEventListener('change', (e) => {
            if (e.target.matches('.fake-checkbox.gallery-item input[type="checkbox"]')) {
                this.updateCheckboxState(e.target);
            }
        });

        // Select All
        if (this.selectAllBtn) {
            this.selectAllBtn.addEventListener('click', () => this.handleSelectAll());
        }

        // Download Button
        if (this.btn) {
            this.btn.addEventListener('click', (e) => this.handleDownload(e));
        }
    },

    updateCheckboxState(checkbox) {
        const parent = checkbox.closest('.fake-checkbox');
        if (checkbox.checked) {
            parent.classList.add('active');
        } else {
            parent.classList.remove('active');
        }
        this.updateDownloadButton();
    },

    updateDownloadButton() {
        if (!this.btn) return;

        const checkedCount = document.querySelectorAll('.fake-checkbox.gallery-item input:checked').length;
        this.btn.disabled = checkedCount === 0;

        if (checkedCount > 0) {
            this.btn.classList.remove('disabled:bg-slate-400');
            this.btn.textContent = `Download Selected (${checkedCount})`;
        } else {
            this.btn.classList.add('disabled:bg-slate-400');
            this.btn.textContent = 'Download Selected';
        }
    },

    handleSelectAll() {
        const checkboxes = document.querySelectorAll('.fake-checkbox.gallery-item input[type="checkbox"]');
        const allChecked = Array.from(checkboxes).every((cb) => cb.checked);
        const newState = !allChecked;

        checkboxes.forEach((cb) => {
            cb.checked = newState;
            this.updateCheckboxState(cb);
        });

        if (this.selectAllBtn) {
            this.selectAllBtn.textContent = newState ? 'Deselect All' : 'Select All';
        }
    },

    handleDownload(e) {
        e.preventDefault();
        const selectedIds = [];
        document.querySelectorAll('.fake-checkbox.gallery-item input:checked').forEach((input) => {
            if (input.dataset.id) selectedIds.push(input.dataset.id);
        });

        if (selectedIds.length === 0) return;

        if (this.mediaIdsInput && this.form) {
            this.mediaIdsInput.value = selectedIds.join(',');
            this.form.submit();
        }
    },
};

// ============================================================================
// JQUERY ENHANCED FEATURES
// ============================================================================

document.addEventListener('DOMContentLoaded', () => {
    GalleryManager.init();
});

(function ($) {
    $(document).ready(function () {
        ModalManager.init();
        AttachmentManager.init();
        GalleryManager.init();

        // Escape key to close modal
        $(document).on('keydown', function (e) {
            if (e.key === 'Escape') {
                const modals = document.querySelectorAll('[data-modal]');
                modals.forEach((modal) => {
                    if (!modal.classList.contains('hidden')) {
                        modal.classList.add('hidden');
                        document.body.style.overflow = '';
                    }
                });
            }
        });

        // Gallery download functionality
        $(document).on('click', '.gallery-download-btn', function (event) {
            const $this = $(this);
            const urls = $this.data('urls');
            const name = $this.data('name');

            const isAnchor = this.tagName === 'A';
            const href = isAnchor ? this.getAttribute('href') : null;
            const hasDirectDownloadHref = isAnchor && this.hasAttribute('download') && href && href !== '#';

            if (hasDirectDownloadHref && !urls) {
                return;
            }

            event.preventDefault();

            if (urls && name) {
                // Single file download
                const url = Array.isArray(urls) ? urls[0] : urls;
                AttachmentManager.downloadFile(url, name);
            } else {
                // Multiple files download
                const files = [];
                $('.gallery-item-wrapper input[data-selected-image]:checked').each(function () {
                    const url = $(this).data('selected-image');
                    const filename = url.split('/').pop();
                    files.push({ url, filename });
                });

                if (files.length > 0) {
                    AttachmentManager.downloadMultipleFilesAsZip(files);
                }
            }
        });

        // Lead dashboard dropdown functionality
        $(document).on('click', '[data-report="lead-dashboard"], #lead-dashboard-btn', function () {
            const dropdown = $('#lead-dashboard-dropdown');

            // Close any existing dropdowns first
            $('.dropdown-menu').not(dropdown).hide();

            // Only show the dropdown if it exists and is currently hidden
            if (dropdown.length && !dropdown.is(':visible')) {
                dropdown.show();

                // Position the dropdown
                const button = $(this);
                const buttonOffset = button.offset();
                dropdown.css({
                    top: buttonOffset.top + button.outerHeight(),
                    left: buttonOffset.left - dropdown.outerWidth() + button.outerWidth(),
                });
            }
        });

        // Close dropdown when clicking outside
        $(document).on('click', function (e) {
            if (
                !$(e.target).closest('[data-report="lead-dashboard"], #lead-dashboard-btn, #lead-dashboard-dropdown')
                    .length
            ) {
                $('#lead-dashboard-dropdown').hide();
            }
        });

        // Handle "View all notifications" button click
        $(document).on('click', '.view-all-notifications', function () {
            window.location.href = '/notifications';
        });

        // Handle popup notifications on page load
        if ($('.popup-notification').length) {
            setTimeout(function () {
                $('.popup-notification').fadeIn();
            }, 1000);
        }

        // ============================================================================
        // NOTIFICATION DROPDOWN AND MODAL
        // ============================================================================

        // Toggle Notification Dropdown
        $(document).on('click', '.notificationBtn', function (e) {
            e.stopPropagation();
            var dropdown = $(this).next('.notificationDropdown');
            $('.notificationDropdown').not(dropdown).addClass('hidden'); // Close other dropdowns
            dropdown.toggleClass('hidden');
        });

        // Close Notification Dropdown when clicking outside
        $(document).on('click', function (e) {
            if (!$(e.target).closest('.notificationBtn, .notificationDropdown').length) {
                $('.notificationDropdown').addClass('hidden');
            }
        });

        function decodeHtmlEntities(str) {
            if (str == null) return str;
            var textarea = document.createElement('textarea');
            textarea.innerHTML = str;
            return textarea.value;
        }

        // Handle Notification Item Click (Show Modal)
        $(document).on('click', '.notification-item', function (e) {
            e.preventDefault();
            e.stopPropagation();

            var notificationDataAttr = $(this).attr('data-notification');

            if (!notificationDataAttr) {
                console.warn('Notification data is missing');
                return;
            }

            var notificationData;
            try {
                notificationData = JSON.parse(notificationDataAttr);
            } catch (err) {
                try {
                    var decodedNotificationDataAttr = decodeHtmlEntities(notificationDataAttr);
                    notificationData = JSON.parse(decodedNotificationDataAttr);
                } catch (err2) {
                    console.error('Error parsing notification data', err2);
                    return;
                }
            }

            if (notificationData) {
                $('.notificationDropdown').addClass('hidden');
                if (window.notificationModal && typeof window.notificationModal.openNotification === 'function') {
                    window.notificationModal.openNotification(notificationData, { mode: 'read' });
                }
            }
        });

        // Open a gated announcement in pop-up mode, where the video must be
        // watched through before it can be dismissed.
        $(document).on('click', '.watch-announcement', function (e) {
            e.preventDefault();
            e.stopPropagation();

            var payloadAttr = $(this).attr('data-notification');
            if (!payloadAttr) return;

            var payload;
            try {
                payload = JSON.parse(payloadAttr);
            } catch (err) {
                try {
                    payload = JSON.parse(decodeHtmlEntities(payloadAttr));
                } catch (err2) {
                    console.error('Error parsing notification data', err2);
                    return;
                }
            }

            if (
                payload &&
                window.notificationModal &&
                typeof window.notificationModal.openNotification === 'function'
            ) {
                window.notificationModal.openNotification(payload, { mode: 'popup' });
            }
        });

        // View All / Show Less Logic
        $(document).on('click', '.view-all-notifications-btn', function (e) {
            e.stopPropagation();
            var container = $(this).closest('.notificationDropdown');
            container.find('.additional-notification').removeClass('hidden');
            $(this).addClass('hidden');
            container.find('.show-less-notifications-btn').removeClass('hidden');
        });

        $(document).on('click', '.show-less-notifications-btn', function (e) {
            e.stopPropagation();
            var container = $(this).closest('.notificationDropdown');
            container.find('.additional-notification').addClass('hidden');
            $(this).addClass('hidden');
            container.find('.view-all-notifications-btn').removeClass('hidden');
        });

        if ($.fancybox) {
            $('[data-fancybox]:not([data-fancybox="preview"])').fancybox({
                buttons: ['close'],
                protect: true,
            });

            $('[data-fancybox="preview"]').fancybox({
                buttons: ['close'],
                protect: true,
                baseClass: 'fancybox-preview',
                iframe: {
                    preload: false,
                },
            });
        }

        // Profile tab switching
        $(document).on('click', '.profile-tab', function () {
            const tab = $(this);
            const target = tab.data('tab-target');

            // Remove active class from all tabs and contents
            $('.profile-tab').removeClass('active');
            $('.profile-tab-content').removeClass('active');

            // Add active class to clicked tab and corresponding content
            tab.addClass('active');
            $(target).addClass('active');
        });

        // Order modal functions
        $(document).on('click', '[data-order-modal]', function () {
            const modalId = $(this).data('order-modal');
            const modal = document.getElementById(modalId);
            if (modal) {
                if (modal.classList.contains('order-modal')) {
                    modal.classList.add('active');
                } else {
                    modal.classList.remove('hidden');
                }
                document.body.style.overflow = 'hidden';
            }
        });

        const getOrderModalEl = () => document.getElementById('order-modal');

        const normalizeOrderModalText = (value) => {
            return String(value || '')
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();
        };

        const setOrderModalStep = (modal, stepIndex) => {
            const steps = Array.from(modal.querySelectorAll('.order-form-step'));
            if (steps.length === 0) return;

            const idx = Math.max(0, Math.min(stepIndex, steps.length - 1));
            steps.forEach((step, i) => step.classList.toggle('active', i === idx));

            const modalInner = modal.querySelector('.modal');
            if (modalInner) modalInner.scrollTop = 0;
        };

        const resetOrderModalState = (modal) => {
            setOrderModalStep(modal, 0);

            const response = modal.querySelector('.response');
            if (response) response.innerHTML = '';

            modal.querySelectorAll('.is-valid, .is-invalid').forEach((el) => {
                el.classList.remove('is-valid', 'is-invalid');
            });

            modal.querySelectorAll('input[type="checkbox"]').forEach((input) => {
                if (input.checked) {
                    input.checked = false;
                }
                input.dispatchEvent(new Event('change', { bubbles: true }));
            });
        };

        const updateOrderModalHeader = (modal, triggerEl, productKey) => {
            const titleEl = modal.querySelector('.modal-header h2');
            if (titleEl) {
                if (!modal.dataset.defaultTitle) {
                    modal.dataset.defaultTitle = titleEl.textContent.trim();
                }

                const customLabel =
                    triggerEl?.dataset?.customContent ||
                    triggerEl?.getAttribute?.('data-custom-content') ||
                    triggerEl?.getAttribute?.('custom-content') ||
                    '';

                titleEl.textContent = customLabel
                    ? `${modal.dataset.defaultTitle}: ${customLabel}`
                    : modal.dataset.defaultTitle;
            }

            const boardsEl = modal.querySelector('.endurance-hardware-boards');
            if (boardsEl) {
                const customLabel =
                    triggerEl?.dataset?.customContent ||
                    triggerEl?.getAttribute?.('data-custom-content') ||
                    triggerEl?.getAttribute?.('custom-content') ||
                    '';

                const isHardwareBoards =
                    [
                        'endurance_hardware_boards',
                        'classic_hardware_board',
                        'heritage_hardware_board',
                        'architectural_hardware_board',
                    ].includes(String(productKey || '')) ||
                    normalizeOrderModalText(customLabel).includes('hardware board');

                boardsEl.classList.toggle('hidden', !isHardwareBoards);
            }
        };

        const resolveOrderModalProductKey = (modal, triggerEl) => {
            const explicitKey = triggerEl?.dataset?.productKey || triggerEl?.getAttribute?.('data-product-key') || null;
            if (explicitKey) {
                const exact = modal.querySelector(`input[name="details[${explicitKey}]"]`);
                if (exact) return explicitKey;

                const aliases = {
                    endurance_colour_swatches: 'colour_swatches',
                    endurance_homeowner_brochure: 'homeowner_brochure',
                    endurance_homeowner_brochures: 'homeowner_brochure',
                    endurance_trade_catalogue: 'trade_catalogue',
                    endurance_trade_catalogues: 'trade_catalogue',
                    trade_catalogues: 'trade_catalogue',
                    avantal_brochure: 'avantal_brochures',
                    endurance_solid_core_sample: 'solid_core_samples',
                    endurance_solid_core_samples: 'solid_core_samples',
                    endurance_hardware_swatch: 'hardware_swatches',
                    endurance_hardware_swatches: 'hardware_swatches',
                };

                const alias = aliases[String(explicitKey)] || null;
                if (alias) {
                    const aliasInput = modal.querySelector(`input[name="details[${alias}]"]`);
                    if (aliasInput) return alias;
                }
            }

            const customLabel =
                triggerEl?.dataset?.customContent ||
                triggerEl?.getAttribute?.('data-custom-content') ||
                triggerEl?.getAttribute?.('custom-content') ||
                '';
            const normalizedCustom = normalizeOrderModalText(customLabel);
            if (!normalizedCustom) return null;

            const rows = Array.from(modal.querySelectorAll('.product-section h5'));
            for (const h5 of rows) {
                const normalizedRow = normalizeOrderModalText(h5.textContent);
                if (!normalizedRow) continue;
                if (normalizedRow.includes(normalizedCustom) || normalizedCustom.includes(normalizedRow)) {
                    const row = h5.closest('.flex');
                    const input = row?.querySelector('input[type="checkbox"][name^="details["]');
                    const name = input?.getAttribute('name') || '';
                    const match = name.match(/^details\[(.+)\]$/);
                    if (match && match[1]) return match[1];
                }
            }

            return null;
        };

        const openOrderModal = (triggerEl) => {
            const modal = getOrderModalEl();
            if (!modal) return;

            modal.classList.add('active');
            document.body.style.overflow = 'hidden';

            resetOrderModalState(modal);

            const productKey = resolveOrderModalProductKey(modal, triggerEl);
            updateOrderModalHeader(modal, triggerEl, productKey);
            if (productKey) {
                const input = modal.querySelector(`input[name="details[${productKey}]"]`);
                if (input && input.type === 'checkbox') {
                    input.checked = true;
                    input.dispatchEvent(new Event('change', { bubbles: true }));
                }
            }
        };

        const closeOrderModal = () => {
            const modal = getOrderModalEl();
            if (!modal) return;
            modal.classList.remove('active');
            document.body.style.overflow = '';
            setOrderModalStep(modal, 0);
        };

        $(document).on('click', '[name="order-modal"]', function (e) {
            const modal = getOrderModalEl();
            if (!modal) return;
            e.preventDefault();
            e.stopPropagation();
            openOrderModal(this);
        });

        $(document).on('click', '#order-modal .order-modal-close', function (e) {
            e.preventDefault();
            e.stopPropagation();
            closeOrderModal();
        });

        $(document).on('click', '#order-modal', function (e) {
            if (e.target === this) {
                closeOrderModal();
            }
        });

        $(document).on('keydown', function (e) {
            const modal = getOrderModalEl();
            if (!modal) return;
            if (e.key === 'Escape' && modal.classList.contains('active')) {
                closeOrderModal();
            }
        });

        $(document).on('click', '#order-modal [name="order-form-next"], #order-modal .btn-order-next', function (e) {
            e.preventDefault();
            const modal = getOrderModalEl();
            if (!modal) return;
            const steps = Array.from(modal.querySelectorAll('.order-form-step'));
            const currentIdx = Math.max(
                0,
                steps.findIndex((s) => s.classList.contains('active')),
            );
            setOrderModalStep(modal, currentIdx + 1);
        });

        $(document).on(
            'click',
            '#order-modal [name="order-form-previous"], #order-modal .order-form-previous',
            function (e) {
                e.preventDefault();
                const modal = getOrderModalEl();
                if (!modal) return;
                const steps = Array.from(modal.querySelectorAll('.order-form-step'));
                const currentIdx = Math.max(
                    0,
                    steps.findIndex((s) => s.classList.contains('active')),
                );
                setOrderModalStep(modal, currentIdx - 1);
            },
        );

        $(document).on('change', '#order-modal .fake-checkbox input[type="checkbox"]', function () {
            const parent = this.closest('.fake-checkbox');
            if (!parent) return;
            parent.classList.toggle('active', this.checked);
        });

        // AJAX Filter Search
        let searchTimeout;
        $(document).on('input', '[data-ajax-search]', function () {
            const $input = $(this);
            const searchTerm = $input.val();
            const endpoint = $input.data('ajax-search');
            const resultsContainer = $($input.data('results-container'));

            clearTimeout(searchTimeout);
            searchTimeout = setTimeout(function () {
                if (searchTerm.length >= 2) {
                    $.ajax({
                        url: endpoint,
                        method: 'GET',
                        data: { q: searchTerm },
                        success: function (data) {
                            resultsContainer.html(data.html || data);
                        },
                        error: function () {
                            resultsContainer.html('<p class="text-red-500">Search failed. Please try again.</p>');
                        },
                    });
                } else {
                    resultsContainer.empty();
                }
            }, 300);
        });

        // Download file handler
        $(document).on('click', 'a[download]', function (event) {
            const $this = $(this);
            const href = $this.attr('href');

            // If it's a data URL or blob, handle it differently
            if (href.startsWith('data:') || href.startsWith('blob:')) {
                event.preventDefault();
                const filename = $this.attr('download') || 'download';
                AttachmentManager.downloadFile(href, filename);
            }
        });

        // Play button delegation (replaces vanilla JS loop)
        $(document).on('click', '.btn-play, .hero-btn-play', function (e) {
            e.preventDefault();
            const videoSrc = $(this).data('video-src') || $(this).attr('data-videoSrc') || $(this).attr('href');
            if (videoSrc && videoSrc !== '#') {
                ModalManager.open(videoSrc);
            }
        });

        // Anchor links smooth scrolling
        $('a[href*="#"]:not([href="#"])').click(function () {
            if (
                location.pathname.replace(/^\//, '') === this.pathname.replace(/^\//, '') &&
                location.hostname === this.hostname
            ) {
                let target = $(this.hash);
                target = target.length ? target : $('[name=' + this.hash.slice(1) + ']');
                if (target.length) {
                    const scrollContainer = $('#app-scroll-container');
                    if (scrollContainer.length) {
                        const containerTop = scrollContainer.offset()?.top ?? 0;
                        const targetTop = target.offset()?.top ?? 0;
                        const currentScrollTop = scrollContainer.scrollTop() ?? 0;
                        const nextScrollTop = currentScrollTop + (targetTop - containerTop) - 100;

                        scrollContainer.animate({ scrollTop: nextScrollTop }, 500);
                    } else {
                        $('html, body').animate(
                            {
                                scrollTop: target.offset().top - 100,
                            },
                            500,
                        );
                    }
                    return false;
                }
            }
        });

        // Door Designs selector
        $('.door-design-selector').on('click', function () {
            $('.door-design-selector').removeClass('active');
            $(this).addClass('active');
        });

        // Radio toggle
        $('input[type="radio"][name="toggle"]').on('change', function () {
            if ($(this).is(':checked')) {
                $('.radio-toggle').removeClass('bg-primary').addClass('bg-gray-200');
                $(this).closest('.radio-toggle').removeClass('bg-gray-200').addClass('bg-primary');
            }
        });

        // Sidebar navigation
        $('.sidebar-nav-item').on('click', function () {
            $('.sidebar-nav-item').removeClass('active');
            $(this).addClass('active');
        });

        // Mobile navigation
        $('.mobile-nav-item').on('click', function () {
            $('.mobile-nav-item').removeClass('active');
            $(this).addClass('active');
        });

        // Search bar toggle
        $('#show-search-bar').on('click', function () {
            $('#search-bar-mobile').toggleClass('hidden down');
        });

        // Accordion functionality
        $('.accordion-item').click(function () {
            $('.accordion-item').not(this).find('.accordion-icon').removeClass('active');
            $('.accordion-item').not(this).find('.accordion-content').slideUp();
            $(this).find('.accordion-icon').toggleClass('active');
            $(this).find('.accordion-content').slideToggle();
        });

        // Floating number interactions
        $('.floatingNumber_number').on('click', function (e) {
            const $this = $(this);
            const box = $this.parent().find('.floatingNumber_text');
            const condition = box.hasClass('expandedBox');
            let heightAdjuster = $this.parent().attr('style');
            heightAdjuster = heightAdjuster.substring(4, heightAdjuster.indexOf('%'));

            $('.elevated').removeClass('elevated');
            $this.parent().addClass('elevated');

            if (e.pageX > window.innerWidth / 2) box.addClass('alignToRight');
            if (heightAdjuster > 70) box.addClass('alignToTop');

            $('.expandedBox').removeClass('expandedBox').addClass('shrinkedBox');

            if (!condition) {
                setTimeout(() => box.removeClass('shrinkedBox').addClass('expandedBox'), 1);
            }

            if (window.innerWidth < 700 && e.pageX > window.innerWidth / 4 && e.pageX < (window.innerWidth / 4) * 3) {
                box.css('maxWidth', (e.pageX > window.innerWidth / 2 ? window.innerWidth - e.pageX : e.pageX) + 'px');
            }
        });

        // Video play button handlers
        $(document).on('click', '.btn-play, .hero-btn-play', function () {
            const videoSrc = $(this).data('video-src') || $(this).attr('href');
            if (videoSrc) {
                ModalManager.open(videoSrc);
            }
        });

        // Additional JQuery Logic
        if (location.hash) {
            $(location.hash);
        }

        // Door Designs selector (Button implementation)
        $('#door-design-select').change(function () {
            const value = $(this).val();
            $('.door-design').removeClass('active');
            const $selectedDoor = $('.door-design[data-door-style="' + value + '"]');
            $selectedDoor.addClass('active');
            $('.door-design-name').text(value);
            const collectionTitle = $selectedDoor.data('collection-title') || '';
            $('.category-title').text(collectionTitle);
        });

        // Radio toggle functionality (Fake radio implementation)
        $('.fake-radio-toggle').click(function () {
            const $this = $(this);
            $this.parent().find('input').not($this.find('input')).prop('checked', false);
            $this.parent().find('.fake-radio').removeClass('active');

            if ($this.find('input').is(':checked')) {
                $this.find('.fake-radio').addClass('active');
            } else {
                $this.find('.fake-radio').removeClass('active');
            }
        });

        // Sidebar navigation toggler
        $('#main-nav-switch').click(function () {
            $('.endurance-logo').toggleClass('hidden');
            $('.endurance-logo-mobile').toggleClass('hidden');
            $('.sidebar-cta-small').toggleClass('hidden');
            $('.main-nav-list-item-label').toggleClass('hidden');
            $('.main-nav-list-item').toggleClass('collapsed');
            $('.sidebar-cta').toggleClass('hidden');
            $('.sidebar').toggleClass('collapsed');
            $('.main-nav').toggleClass('collapsed');
            $('.main-nav-switch').toggleClass('rotated');
            $('#small-sidebar-logo').toggleClass('hidden');
            $('.main-nav-switch-logo').toggleClass('hidden');
        });

        // Mobile navigation (Vanilla JS implementation)
        const mobileNavOpen = document.getElementById('mobile-navbar-open');
        const mobileNavClose = document.getElementById('mobile-navbar-close');
        const mobileBackdrop = document.getElementById('mobile-sidebar-backdrop');

        if (mobileNavOpen) {
            mobileNavOpen.addEventListener('click', () => toggleMobileSidebar());
        }
        if (mobileNavClose) {
            mobileNavClose.addEventListener('click', () => toggleMobileSidebar());
        }
        if (mobileBackdrop) {
            mobileBackdrop.addEventListener('click', () => closeMobileSidebar());
        }

        function toggleMobileSidebar() {
            document.getElementById('sidebar').classList.toggle('mobile-open');
            document.getElementById('mobile-sidebar-backdrop').classList.toggle('hidden');
        }

        function closeMobileSidebar() {
            document.getElementById('sidebar').classList.remove('mobile-open');
            document.getElementById('mobile-sidebar-backdrop').classList.add('hidden');
        }
    });
})(jQuery);

// ============================================================================
// ALPINE.JS COMPONENTS
// ============================================================================

// Register filters component for Blocks template pages
Alpine.data('filtersData', (options = {}) => ({
    selectedCategories: [],
    selectedProduct: 'all',
    serverSide: Boolean(options.serverSide ?? false),

    init() {
        this.initializeFromUrl();
        if (!this.serverSide) {
            this.$nextTick(() => this.applyFilter());
        }
    },

    initializeFromUrl() {
        const urlParams = new URLSearchParams(window.location.search);
        const filterCat = urlParams.get('filter_category');
        const filterProd = urlParams.get('filter_product');

        const categories = [];
        if (filterCat && filterCat !== 'all') categories.push(filterCat);

        urlParams.getAll('filter_category[]').forEach((v) => {
            if (v && v !== 'all') categories.push(v);
        });

        for (const [key, value] of urlParams.entries()) {
            if (key.startsWith('filter_category[') && value && value !== 'all') {
                categories.push(value);
            }
        }

        this.selectedCategories = Array.from(new Set(categories));
        if (filterProd && filterProd !== 'all') {
            this.selectedProduct = filterProd;
        }
    },

    toggleCategory(category) {
        const index = this.selectedCategories.indexOf(category);
        if (index > -1) {
            this.selectedCategories.splice(index, 1);
        } else {
            this.selectedCategories.push(category);
        }
        if (this.serverSide) {
            this.submitFilters();
        } else {
            this.applyFilter();
        }
    },

    selectProduct(value) {
        this.selectedProduct = value;
        if (this.serverSide) {
            this.submitFilters();
        } else {
            this.applyFilter();
        }
    },

    submitFilters() {
        this.$nextTick(() => {
            const form = this.$root;
            if (form && typeof form.requestSubmit === 'function') {
                form.requestSubmit();
                return;
            }
            if (form && typeof form.submit === 'function') {
                form.submit();
            }
        });
    },

    applyFilter() {
        const container =
            document.querySelector('[data-mixitup-container]') ||
            document.getElementById('technical-materials') ||
            document.getElementById('marketing-materials') ||
            document.getElementById('search-results-container');
        if (!container) {
            console.warn('Filter container not found');
            return;
        }

        const items = container.querySelectorAll('.mix');
        items.forEach((item) => {
            const itemClasses = item.className.toLowerCase().split(/\s+/);
            // const shouldShow = this.shouldShowItem(itemClasses);
            let categoryMatch =
                this.selectedCategories.length === 0 ||
                this.selectedCategories.some((cat) => itemClasses.includes(cat.toLowerCase()));

            // Check product match (if a product is selected)
            let productMatch = this.selectedProduct === 'all';
            if (!productMatch) {
                const productOption = document.querySelector(`#product-filter option[value="${this.selectedProduct}"]`);
                if (productOption) {
                    const filterClasses = (productOption.dataset.filter || '')
                        .split(',')
                        .map((c) => c.trim().replace('.', ''));
                    productMatch = filterClasses.some((fc) => itemClasses.includes(fc.toLowerCase()));
                }

                this.setItemVisibility(item, categoryMatch && productMatch);
            }

            const shouldShow = categoryMatch && productMatch;
            if (shouldShow) {
                item.style.display = '';
                item.style.visibility = 'visible';
                item.style.opacity = '1';
            } else {
                item.style.display = 'none';
                item.style.visibility = 'hidden';
                item.style.opacity = '0';
            }
        });
    },

    shouldShowItem(itemClasses) {
        const categoryMatch =
            this.selectedCategories.length === 0 ||
            this.selectedCategories.some((cat) => itemClasses.includes(cat.toLowerCase()));

        let productMatch = this.selectedProduct === 'all';

        if (!productMatch) {
            const productOption = document.querySelector(`#product-filter option[value="${this.selectedProduct}"]`);
            if (productOption) {
                const filterClasses = (productOption.dataset.filter || '')
                    .split(',')
                    .map((c) => c.trim().replace('.', ''));
                productMatch = filterClasses.some((fc) => itemClasses.includes(fc.toLowerCase()));
            }
        }

        return categoryMatch && productMatch;
    },

    setItemVisibility(item, shouldShow) {
        if (shouldShow) {
            item.style.display = '';
            item.style.visibility = 'visible';
            item.style.opacity = '1';
        } else {
            item.style.display = 'none';
            item.style.visibility = 'hidden';
            item.style.opacity = '0';
        }
    },
}));

Alpine.start();

const UserProfilePageManager = {
    init() {
        const editBtn = document.getElementById('edit-profile-details');
        const passBtn = document.getElementById('change-profile-pass');
        const detailsWrapper = document.querySelector('.user-settings-wrapper');
        const detailsForm = document.querySelector('.profile-details-form');
        const passWrapper = document.querySelector('.user-pass-wrapper');
        const passForm = document.querySelector('.profile-password-change-form');

        if (!editBtn && !passBtn) return;

        const showDetails = () => {
            if (detailsWrapper) detailsWrapper.classList.remove('hidden');
            if (detailsForm) detailsForm.classList.remove('hidden');
            if (passWrapper) passWrapper.classList.add('hidden');
            if (passForm) passForm.classList.add('hidden');
            if (detailsWrapper) detailsWrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
        };

        const showPassword = () => {
            if (passWrapper) passWrapper.classList.remove('hidden');
            if (passForm) passForm.classList.remove('hidden');
            if (detailsWrapper) detailsWrapper.classList.add('hidden');
            if (detailsForm) detailsForm.classList.add('hidden');
            if (passWrapper) passWrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
        };

        if (detailsForm && detailsForm.querySelector('p.text-red-500')) {
            showDetails();
        } else if (passForm && passForm.querySelector('p.text-red-500')) {
            showPassword();
        }

        if (editBtn) {
            editBtn.addEventListener('click', (e) => {
                e.preventDefault();
                showDetails();
            });
        }

        if (passBtn) {
            passBtn.addEventListener('click', (e) => {
                e.preventDefault();
                showPassword();
            });
        }
    },
};

// ============================================================================
// INITIALIZATION
// ============================================================================

document.addEventListener('DOMContentLoaded', function () {
    if (!window.notificationModal) {
        const rootId = 'notification-modal-app';
        let root = document.getElementById(rootId);
        if (!root) {
            root = document.createElement('div');
            root.id = rootId;
            document.body.appendChild(root);
        }
        const notificationModalApp = createApp(NotificationModal);
        window.notificationModal = notificationModalApp.mount(root);
    }

    // Initialize core managers
    ModalManager.init();
    PopupNotificationManager.init();
    AttachmentManager.init();
    GalleryManager.init();
    UserProfilePageManager.init();

    // Expose managers to global scope
    window.ModalManager = ModalManager;
    window.AttachmentManager = AttachmentManager;
    window.GalleryManager = GalleryManager;
    window.UserProfilePageManager = UserProfilePageManager;
    window.getCsrfToken = getCsrfToken;
    window.SubmissionModalManager = SubmissionModalManager;
    window.AlertManager = AlertManager;
    window.showAlert = AlertManager.showAlert;
    window.CarouselManager = CarouselManager;

    // Initialize carousel system
    CarouselManager.init();

    // Initialize poll voting system
    if (typeof PollManager !== 'undefined') {
        PollManager.init();
    }

    // Add keyboard shortcuts
    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
            SubmissionModalManager.close();
        }
    });

    // Handle global static alerts close button
    document.addEventListener('click', function (e) {
        const closeBtn = e.target.closest('.alert-close');
        if (closeBtn) {
            const alert = closeBtn.closest('.alert');
            if (alert) {
                alert.remove();
            }
        }
    });
});

// ============================================================================
// EXPORT FOR MODULE USAGE
// ============================================================================

export {
    ModalManager,
    AttachmentManager,
    getCsrfToken,
    SubmissionModalManager,
    AlertManager,
    CarouselManager,
    GlobalAlertManager,
    GlobalCarouselManager,
    PopupNotificationManager,
};
